import type { DooTableName } from '../types';
import { dooTable } from './client';
import { throwOnError } from './errors';

export interface JunctionSpec {
  table: DooTableName;
  parentColumn: string;
  childColumn: string;
}

export const MAPPING_COLOUR_JUNCTION: JunctionSpec = {
  table: 'doo_door_style_colours',
  parentColumn: 'mapping_id',
  childColumn: 'colour_id',
};

export const MAPPING_GLASS_JUNCTION: JunctionSpec = {
  table: 'doo_door_style_glass',
  parentColumn: 'mapping_id',
  childColumn: 'glass_id',
};

export const MAPPING_HARDWARE_JUNCTION: JunctionSpec = {
  table: 'doo_door_style_hardware',
  parentColumn: 'mapping_id',
  childColumn: 'hardware_id',
};

/** @deprecated Use MAPPING_*_JUNCTION — parent is now mapping_id. */
export const DOOR_STYLE_COLOUR_JUNCTION = MAPPING_COLOUR_JUNCTION;
/** @deprecated Use MAPPING_*_JUNCTION — parent is now mapping_id. */
export const DOOR_STYLE_GLASS_JUNCTION = MAPPING_GLASS_JUNCTION;
/** @deprecated Use MAPPING_*_JUNCTION — parent is now mapping_id. */
export const DOOR_STYLE_HARDWARE_JUNCTION = MAPPING_HARDWARE_JUNCTION;

export const HARDWARE_FINISH_JUNCTION: JunctionSpec = {
  table: 'doo_hardware_colours',
  parentColumn: 'hardware_id',
  childColumn: 'finish_id',
};

/** @deprecated Use HARDWARE_FINISH_JUNCTION */
export const HARDWARE_COLOUR_JUNCTION = HARDWARE_FINISH_JUNCTION;

export function junctionDiff(currentIds: string[], nextIds: string[]): { add: string[]; remove: string[] } {
  const current = new Set(currentIds);
  const next = new Set(nextIds);
  return {
    add: nextIds.filter((id) => !current.has(id)),
    remove: currentIds.filter((id) => !next.has(id)),
  };
}

export async function listChildIds(spec: JunctionSpec, parentId: string): Promise<string[]> {
  const result = await dooTable(spec.table)
    .select(spec.childColumn)
    .eq(spec.parentColumn, parentId);
  const rows =
    (throwOnError(result, `Failed to load ${spec.table}`) as unknown as Array<Record<string, string>>) ?? [];
  return rows.map((row) => row[spec.childColumn]);
}

export async function listAllJunctionRows(spec: JunctionSpec): Promise<Array<Record<string, string>>> {
  const result = await dooTable(spec.table).select('*');
  return (throwOnError(result, `Failed to load ${spec.table}`) as Array<Record<string, string>>) ?? [];
}

export async function syncJunction(spec: JunctionSpec, parentId: string, nextIds: string[]): Promise<void> {
  const currentIds = await listChildIds(spec, parentId);
  const { add, remove } = junctionDiff(currentIds, nextIds);

  if (add.length) {
    const rows = add.map((childId) => ({
      [spec.parentColumn]: parentId,
      [spec.childColumn]: childId,
    }));
    const inserted = await dooTable(spec.table).insert(rows as never);
    throwOnError(inserted, `Failed to add ${spec.table} rows`);
  }

  if (remove.length) {
    const deleted = await dooTable(spec.table)
      .delete()
      .eq(spec.parentColumn, parentId)
      .in(spec.childColumn, remove);
    throwOnError(deleted, `Failed to remove ${spec.table} rows`);
  }
}

export async function setJunctionPair(
  spec: JunctionSpec,
  parentId: string,
  childId: string,
  allowed: boolean
): Promise<void> {
  if (allowed) {
    const current = await listChildIds(spec, parentId);
    if (current.includes(childId)) return;
    const inserted = await dooTable(spec.table).insert({
      [spec.parentColumn]: parentId,
      [spec.childColumn]: childId,
    } as never);
    throwOnError(inserted, `Failed to add ${spec.table} row`);
    return;
  }

  const deleted = await dooTable(spec.table)
    .delete()
    .eq(spec.parentColumn, parentId)
    .eq(spec.childColumn, childId);
  throwOnError(deleted, `Failed to remove ${spec.table} row`);
}

export async function loadMappingCompatibility(mappingId: string): Promise<{
  colourIds: string[];
  glassIds: string[];
  hardwareIds: string[];
}> {
  const [colourIds, glassIds, hardwareIds] = await Promise.all([
    listChildIds(MAPPING_COLOUR_JUNCTION, mappingId),
    listChildIds(MAPPING_GLASS_JUNCTION, mappingId),
    listChildIds(MAPPING_HARDWARE_JUNCTION, mappingId),
  ]);
  return { colourIds, glassIds, hardwareIds };
}

export async function saveMappingCompatibility(
  mappingId: string,
  next: { colourIds: string[]; glassIds: string[]; hardwareIds: string[] }
): Promise<void> {
  await syncJunction(MAPPING_COLOUR_JUNCTION, mappingId, next.colourIds);
  await syncJunction(MAPPING_GLASS_JUNCTION, mappingId, next.glassIds);
  await syncJunction(MAPPING_HARDWARE_JUNCTION, mappingId, next.hardwareIds);
}

export async function saveHardwareFinishes(hardwareId: string, finishIds: string[]): Promise<void> {
  await syncJunction(HARDWARE_FINISH_JUNCTION, hardwareId, finishIds);
}

/** @deprecated Use saveHardwareFinishes */
export async function saveHardwareColours(hardwareId: string, finishIds: string[]): Promise<void> {
  return saveHardwareFinishes(hardwareId, finishIds);
}
